import type { TFunction } from "i18next";

import { PongData } from "~/features/andon/andon.types";

export function getBrowserBadge(browserRaw?: string) {
  const browser = (browserRaw || "").toLowerCase();
  switch (true) {
    case browser.includes("chrome"):
      return { label: "Chrome", color: "#e3f2fd", border: "#1976d2" };
    case browser.includes("edge"):
      return { label: "Edge", color: "#e6f4ea", border: "#2186c6" };
    case browser.includes("firefox"):
      return { label: "Firefox", color: "#fff3e0", border: "#e65100" };
    case browser.includes("safari"):
      return { label: "Safari", color: "#e8f5e9", border: "#43a047" };
    case browser.includes("opera"):
      return { label: "Opera", color: "#fce4ec", border: "#d81b60" };
    case browser.includes("brave"):
      return { label: "Brave", color: "#fff8e1", border: "#ff9800" };
    default:
      return {
        label: browserRaw || "--",
        color: "#f5f5f5",
        border: "#bdbdbd"
      };
  }
}

export function getOSBadge(osRaw?: string) {
  const os = (osRaw || "").toLowerCase();
  switch (true) {
    case os.includes("windows"):
      return { label: "Windows", color: "#e3f2fd", border: "#1976d2" };
    case os.includes("mac"):
    case os.includes("osx"):
      return { label: "Mac", color: "#f3e5f5", border: "#6d4c41" };
    case os.includes("linux"):
      return { label: "Linux", color: "#e0f2f1", border: "#009688" };
    case os.includes("android"):
      return { label: "Android", color: "#f1f8e9", border: "#388e3c" };
    case os.includes("ios"):
      return { label: "iOS", color: "#ede7f6", border: "#3949ab" };
    default:
      return { label: osRaw || "--", color: "#f5f5f5", border: "#bdbdbd" };
  }
}

export function formatBytes(input?: number | null): string {
  if (input === undefined || input === null) return "";
  if (input < 1024) return `${input} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let i = 0;
  let value = input / 1024;
  while (value >= 1024 && i < units.length - 1) {
    value = value / 1024;
    i++;
  }
  return `${value.toFixed(2)} ${units[i]}`;
}

export const getColorForValue = (
  value: number,
  type: "fps" | "rtt" | "downlink"
): "green" | "orange" | "red" => {
  if (type === "fps") {
    if (value >= 50) return "green";
    if (value >= 30) return "orange";
    return "red";
  }
  if (type === "rtt") {
    if (value <= 100) return "green";
    if (value <= 200) return "orange";
    return "red";
  }
  if (type === "downlink") {
    if (value >= 10) return "green";
    if (value >= 3) return "orange";
    return "red";
  }
  return "green";
};

export const getMemoryColor = (memory?: PongData["memory"]) => {
  if (!memory || !memory.usedJSHeapSize || !memory.jsHeapSizeLimit) {
    return undefined;
  }

  const percent = (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100;

  if (percent <= 50) return "green";
  if (percent <= 80) return "orange";
  return "red";
};

export function getMinMaxAvg(data: number[]) {
  if (data.length === 0) return { min: "0", max: "0", avg: "0" };
  const sum = data.reduce((a, b) => a + b, 0);
  return {
    min: Math.min(...data).toFixed(1),
    max: Math.max(...data).toFixed(1),
    avg: (sum / data.length).toFixed(1)
  };
}

export const calculateMemoryUsagePercentage = (memory?: PongData["memory"]) => {
  if (!memory || !memory.usedJSHeapSize || !memory.jsHeapSizeLimit) {
    return "";
  }

  const percent = (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100;
  return `${percent.toFixed(1)}`;
};

export function formatMsOrSec(value: number | string): {
  value: string;
  unit: string;
} {
  const num = typeof value === "number" ? value : Number(value);
  if (isNaN(num) || num <= 0) return { value: "", unit: "" };
  if (num < 1000) return { value: `${Math.round(num)}`, unit: "ms" };
  if (num < 60_000) return { value: `${(num / 1000).toFixed(2)}`, unit: "s" };
  // 1 min = 60000 ms
  if (num < 3600_000) {
    const minutes = Math.floor(num / 60_000);
    const seconds = ((num % 60_000) / 1000).toFixed(0);
    return { value: `${minutes}m ${seconds.padStart(2, "0")}s`, unit: "" };
  }
  // Over an hour, just show minutes
  const minutes = Math.round(num / 60_000);
  return { value: `${minutes}m`, unit: "m" };
}

export function formatMinMaxAvgT({
  t,
  min,
  max,
  avg,
  minUnit = "",
  maxUnit = "",
  avgUnit = ""
}: {
  t: TFunction;
  min: string | number;
  max: string | number;
  avg: string | number;
  minUnit?: string;
  maxUnit?: string;
  avgUnit?: string;
}): string {
  const formatVal = (val: string | number, unit: string) =>
    typeof val === "number" || (typeof val === "string" && val !== "")
      ? `${val}${unit ? `${unit}` : ""}`
      : "";
  return `${t("min")}: ${formatVal(min, minUnit)} | ${t("max")}: ${formatVal(max, maxUnit)} | ${t("avg")}: ${formatVal(avg, avgUnit)}`;
}

export function hasValidHistory(history: number[]): boolean {
  return history.some(
    (val) => typeof val === "number" && val > 0 && !isNaN(val)
  );
}
